import { firestore, storage } from '.';
import { buildObjectsTree, buildObject } from './helpers/firestore';

export const newDocument = ({ path }) => {
  if (!path) throw Error('Path must be specified');
  return firestore()
    .collection(path)
    .doc();
};

export const startDocumentListener = ({ path }, next, error, complete) => {
  if (!path) error('Path must be specified');
  const ref = firestore().doc(path);
  return ref.onSnapshot(doc => next(buildObject(doc)), error, complete);
};

export const startCollectionListener = (
  { path, conditions = [], orderBy = [], limit = null, startAfter = null },
  next,
  error,
  complete
) => {
  if (!path) error('Path must be specified');
  let ref = firestore().collection(path);

  if (startAfter && (!limit || !orderBy))
    error('OrderBy and limit are required during paginated calls');

  orderBy.forEach(order => {
    const { field, direction } = order;
    if (field && direction) ref = ref.orderBy(field, direction);
  });

  conditions.forEach(condition => {
    const { field, operation, value } = condition;
    if (field && operation && value) ref = ref.where(field, operation, value);
  });

  if (limit && typeof limit === 'number') ref = ref.limit(limit);

  if (startAfter && orderBy && startAfter.id) ref = ref.startAfter(startAfter);

  return ref.onSnapshot(
    querySnapshot => next(buildObjectsTree(querySnapshot.docs)),
    error,
    complete
  );
};

export const getDocument = async ({ path }) => {
  try {
    if (!path) throw Error('Path must be specified');
    const ref = firestore().doc(path);
    const doc = await ref.get();
    return buildObject(doc);
  } catch (error) {
    console.log(error.message);
    throw Error(error.message);
  }
};

export const getDocuments = async ({
  path,
  conditions = [],
  orderBy = [],
  limit = null,
  startAfter = null,
}) => {
  try {
    if (!path) throw Error('Path must be specified');
    let ref = firestore().collection(path);

    if (startAfter && (!limit || !orderBy))
      throw Error('OrderBy and limit are required during paginated calls');

    orderBy.forEach(order => {
      const { field, direction } = order;
      if (field && direction) ref = ref.orderBy(field, direction);
    });

    conditions.forEach(condition => {
      const { field, operation, value } = condition;
      if (field && operation && value) ref = ref.where(field, operation, value);
    });

    if (limit && typeof limit === 'number') ref = ref.limit(limit);

    if (startAfter && orderBy && startAfter.id)
      ref = ref.startAfter(startAfter);

    const snapshot = await ref.get();
    const lastDocument = snapshot.docs[snapshot.docs.length - 1];

    return {
      data: buildObjectsTree(snapshot.docs) || {},
      lastDocument: lastDocument || null,
    };
  } catch (error) {
    console.log(JSON.stringify(error));
    throw Error(error.message);
  }
};

export const addDocument = async ({ path, data }) => {
  if (!path) throw Error('Path must be specified');
  if (!data) throw Error('Data cannot be empty');
  const ref = firestore().collection(path);
  return ref.add(data);
};

export const updateDocument = async ({ path, data }) => {
  if (!path) throw Error('Path must be specified');
  if (!data) throw Error('Data cannot be empty');
  const ref = firestore().doc(path);
  return ref.update(data);
};

export const deleteDocument = ({ path }) => {
  if (!path) throw Error('Path must be specified');
  const ref = firestore().doc(path);
  return ref.delete();
};

export const setDocument = ({ path, data, merge = true }) => {
  if (!path) throw Error('Path must be specified');
  if (!data) throw Error('Data cannot be empty');
  const ref = firestore().doc(path);
  return ref.set(data, { merge });
};

export const downloadFile = async ({ path, refId }) => {
  try {
    if (!path) throw Error('Path must be specified');
    if (!refId) throw Error('refId cannot be empty');
    const storageRef = storage()
      .ref(path)
      .child(refId);
    const downloadUrl = await storageRef.getDownloadURL();
    return downloadUrl;
  } catch (error) {
    throw Error(error.message);
  }
};

export const deleteFile = ({ path, refId }) => {
  if (!path) throw Error('Path must be specified');
  if (!refId) throw Error('refId cannot be empty');
  const storageRef = storage()
    .ref(path)
    .child(refId);
  return storageRef.delete();
};
